import { LucideMail, LucidePhone, LucideSmartphone } from 'lucide-react'
import React, { useMemo } from 'react'
import { Avatar, Button } from '~/components/ui'
import { Listing, type ListingBroker, type ModelCompany } from '~/models'
import { cn, fmtName, fmtPhoneNumber, getPhoneHref } from '~/utils'

type ListingBrokersProps = {
  className?: string
  listing: Listing
  children?: React.ReactNode
}

export default function ListingBrokers(props: ListingBrokersProps) {
  const { className, listing, children } = props

  const brokersByCompany = useMemo(() => {
    const brokersByCompany: {
      company: ModelCompany
      brokers: ListingBroker[]
    }[] = [
      {
        company: listing.company,
        brokers: [],
      },
    ]
    listing.brokers.forEach((broker) => {
      if (broker.status === 'inactive') return
      const companyIndex = brokersByCompany.findIndex(
        (item) => item.company._id === broker.company._id
      )
      if (companyIndex === -1) {
        brokersByCompany.push({
          company: broker.company,
          brokers: [broker],
        })
      } else {
        brokersByCompany[companyIndex].brokers.push(broker)
      }
    })
    return brokersByCompany
  }, [listing])

  return (
    <>
      <div className={cn('flex flex-col gap-4', className)}>
        {brokersByCompany.map((item) => (
          <CompanyBrokers
            key={item.company._id}
            company={item.company}
            brokers={item.brokers}
            isPrimaryBrokerage={item.company._id === listing.company._id}
          />
        ))}
      </div>
      {children}
    </>
  )
}

function CompanyBrokers({
  company,
  brokers,
  isPrimaryBrokerage,
}: {
  company: ModelCompany
  brokers: ListingBroker[]
  isPrimaryBrokerage: boolean
}) {
  return (
    <>
      <h4
        className={cn(
          'mb-4 font-medium',
          isPrimaryBrokerage ? 'text-xl' : 'text-base'
        )}>
        <a
          className="underline-offset-4 hover:underline"
          href={`/companies/${company.slug}`}
          target="_blank">{`${company.name}`}</a>
      </h4>
      {brokers.length === 0 && (
        <>
          <p className="mb-8 italic">No active brokers with {company.name}</p>
          <p className="mb-4 text-lg font-semibold">Other Brokers</p>
        </>
      )}
      {brokers.map((broker, index) => {
        if (broker.status === 'inactive') {
          return (
            <div
              key={index}
              className="mb-4 border-b pb-4 last:mb-0 last:border-b-0 last:pb-0">
              <p className="mb-1 text-lg font-medium">
                {fmtName(broker.firstName, broker.middleName, broker.lastName)}
              </p>
              <p className="text-sm italic">
                Broker no longer with {company.name}
              </p>
            </div>
          )
        }
        return (
          <div
            key={index}
            className="mb-4 flex gap-6 border-b pb-4 last:mb-0 last:border-b-0 last:pb-0">
            <div className="">
              <div className="flex items-center">
                <Avatar className="aspect-square w-10 rounded-full bg-background object-contain ring-2 ring-muted">
                  {broker.meta?.photo && (
                    <Avatar.Image
                      src={broker.meta!.photo!.thumbUrl}
                      alt={`${broker.firstName} ${broker.lastName}`}
                      loading="lazy"
                    />
                  )}
                  <Avatar.Fallback className="rounded-none bg-transparent">
                    {[broker.firstName.slice(0, 1), broker.lastName.slice(0, 1)]
                      .join('')
                      .toUpperCase()}
                  </Avatar.Fallback>
                </Avatar>
                <div className="mb-1 ml-3">
                  <a
                    className="text-lg font-medium underline-offset-4 hover:underline"
                    href={`/brokers/${broker.slug}`}
                    target="_blank">
                    {fmtName(
                      broker.firstName,
                      broker.middleName,
                      broker.lastName
                    )}
                  </a>
                  {broker.meta?.memberships && (
                    <span className="ml-2 text-base text-muted-foreground">
                      {broker.meta.memberships.split(/\s+|,\s+|\|/).join(', ')}
                    </span>
                  )}
                  {broker.meta?.title && <p>{broker.meta?.title}</p>}
                </div>
              </div>
              {broker.email && (
                <div className="ml-3 flex items-center">
                  <LucideMail className="mr-3 h-5 w-5 text-muted-foreground" />
                  <Button asChild variant="link" className="px-0 text-base">
                    <a href={`mailto:${broker.email}`} target="_blank">
                      {broker.email}
                    </a>
                  </Button>
                </div>
              )}
              {broker.meta?.office && (
                <div className="ml-3 flex items-center">
                  <LucidePhone className="mr-3 h-5 w-5 text-muted-foreground" />
                  <Button asChild variant="link" className="px-0 text-base">
                    <a
                      href={getPhoneHref(
                        broker.meta.office,
                        broker.meta.officeExtension
                      )}
                      target="_blank">
                      {fmtPhoneNumber(
                        broker.meta.office,
                        broker.meta.officeExtension
                      )}
                    </a>
                  </Button>
                </div>
              )}
              {broker.meta?.mobile && (
                <div className="ml-3 flex items-center">
                  <LucideSmartphone className="mr-3 h-5 w-5 text-muted-foreground" />
                  <Button asChild variant="link" className="px-0 text-base">
                    <a href={`tel:${broker.meta.mobile}`} target="_blank">
                      {fmtPhoneNumber(broker.meta.mobile)}
                    </a>
                  </Button>
                </div>
              )}
            </div>
          </div>
        )
      })}
    </>
  )
}
